/**
 * <mango-completed> — the completed work-orders list + earnings summary.
 * =====================================================================
 * Lists the provider's work orders filtered to provider_status === complete and,
 * for an ADMIN response, renders:
 *   - A green "Total earnings" hero (flat fee + overage comp + FMV margin)
 *   - A date-range selector (Last 30 / 60 / 90 / 180 / 365 days, All time)
 *   - Three summary cards: work order count · total FMV received · e-waste volume
 *   - Per-row financial columns: Total earnings · Overage earnings · Total FMV value
 *     · Equipment purchase price
 *
 * NOTE ON FILTERING: the contract's list endpoint exposes search / ordering /
 * page / limit, but NO `status` query parameter — so we filter client-side over
 * the (queryset-scoped) list rather than invent a `?status=` the contract lacks.
 *
 * DATE RANGE: also client-side only — no `?after=` param in the contract. We
 * re-filter the loaded `completed` list by completedOn(job) each time the
 * selector changes. Default window: 60 days.
 *
 * ROLE GATING is by FIELD PRESENCE, not a client-side role flag: a Staff response
 * omits `fmv_breakdown` / `net_settlement` (FINANCIAL_FIELDS), so `hasFinancials()`
 * is false and we strip the admin section + every `.admin-col` cell. The server is
 * the real enforcement; this only avoids showing Staff empty/forbidden columns.
 *
 * COLUMN MAPPING (all existing contract fields — no invented binds):
 *   Total earnings        = sow_rate + provider_overage_comp + (total_assessed_fmv − equipment_purchase_price)
 *   Overage earnings      = net_settlement.provider_overage_comp   (shown positive, green)
 *   Total FMV value       = fmv_breakdown.total_assessed_fmv       (neutral)
 *   Equipment purchase    = net_settlement.equipment_purchase_price (neutral)
 *   Hero "Total earnings" = Σ of per-row total earnings (filtered by date range)
 *   Sum FMV card          = Σ fmv_breakdown.total_assessed_fmv
 *   Sum e-waste card      = Σ weight_ewaste (lbs)
 *
 * Export CSV builds the settlement history client-side from the already-loaded data
 * (no endpoint). Request reopen is a MOCK acknowledgement — there is no settlement-
 * status / reopen backend yet (billing phase). Both are wired via addEventListener
 * (CSP-safe), never inline onclick.
 *
 * fmv_2 NEVER appears: the WorkOrder type has no such field; no bind references it.
 */
import { listWorkOrders, type WorkOrder } from "../api/client";
import { applyBindings } from "../bind";
import { loadView, date, setText, money, hasFinancials, toggle } from "./shared";

const VIEW_URL = new URL("../../views/completed.html", import.meta.url).href;

/** Default date-range window in days (0 = all time). */
const DEFAULT_DAYS = 60;

/**
 * Inject the CSS for the earnings hero and date-range dropdown once into the
 * document head. Uses a sentinel id so multiple component instantiations are safe.
 * style-src 'unsafe-inline' is already in the portal CSP (vite.config.ts).
 */
const STYLE_ID = "mango-completed-styles";
function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
/* mango-completed: earnings hero */
.earnings-hero{background:linear-gradient(135deg,var(--green-light),#F0FAF4);border:1px solid var(--green-border);border-radius:var(--r-lg);padding:20px 24px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-start;}
.earnings-hero-amount{font-size:38px;font-weight:700;color:var(--green);font-family:var(--mono);line-height:1;}
.earnings-hero-label{font-size:13px;color:var(--green);font-weight:600;margin-top:8px;letter-spacing:.01em;}
.earnings-hero-foot{font-size:11.5px;color:var(--text-2);margin-top:6px;max-width:520px;line-height:1.5;}
/* mango-completed: date-range selector */
.daterange-selector{position:relative;}
.daterange-btn{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:500;color:var(--text);background:var(--surface);border:1px solid var(--border-2);border-radius:var(--r-sm);padding:6px 11px;cursor:pointer;white-space:nowrap;}
.daterange-btn:hover{background:var(--surface-2);}
.daterange-menu{position:absolute;right:0;top:calc(100% + 4px);background:var(--surface);border:1px solid var(--border);border-radius:var(--r-md);box-shadow:0 4px 16px rgba(0,0,0,.12);list-style:none;padding:4px 0;min-width:160px;z-index:200;}
.daterange-menu li{padding:8px 14px;font-size:13px;color:var(--text);cursor:pointer;white-space:nowrap;}
.daterange-menu li:hover{background:var(--surface-2);}
.daterange-menu li[aria-selected="true"]{font-weight:600;color:var(--green);}
/* mango-completed: right-align numeric table column headers */
.comp-table th.r{text-align:right;}
@media(max-width:640px){
  .earnings-hero-amount{font-size:28px;}
  .earnings-hero{padding:16px;}
}
  `;
  document.head.appendChild(el);
}

/** The settlement-completion date for a finished WO (purchase → Mango approval). */
function completedOn(job: WorkOrder): string {
  return date(job.mango_approved_at ?? job.finalized_at ?? job.dropoff_datetime);
}

/**
 * Parse a completedOn display string back to a Date for range comparison.
 * We keep a parallel raw-ISO getter so we don't re-parse formatted strings.
 */
function completedAt(job: WorkOrder): Date {
  const iso = job.mango_approved_at ?? job.finalized_at ?? job.dropoff_datetime;
  return new Date(iso ?? 0);
}

/**
 * Compute the per-row "Total earnings" figure from existing contract fields:
 *   sow_rate (flat $235 initiation fee)
 *   + provider_overage_comp (above-ceiling vendor overage comp)
 *   + (total_assessed_fmv − equipment_purchase_price) (FMV margin: assessed − purchase)
 *
 * If any financial sub-field is missing (Staff view), returns null.
 */
function computeTotalEarnings(job: WorkOrder): number | null {
  const flat = Number(job.sow_rate ?? 0);
  const overageComp = Number(job.net_settlement?.provider_overage_comp ?? 0);
  const totalFmv = Number(job.fmv_breakdown?.total_assessed_fmv ?? 0);
  const purchasePrice = Number(job.net_settlement?.equipment_purchase_price ?? 0);
  // Return null only if ALL financial fields are absent (true Staff response).
  if (
    job.sow_rate === undefined &&
    job.net_settlement === undefined &&
    job.fmv_breakdown === undefined
  ) {
    return null;
  }
  return flat + overageComp + (totalFmv - purchasePrice);
}

/** Human label for the selected date-range window. */
function rangeLabel(days: number): string {
  if (days === 0) return "all time";
  if (days === 365) return "last 12 months";
  if (days === 180) return "last 6 months";
  return `last ${days} days`;
}

export class MangoCompleted extends HTMLElement {
  /** All completed work orders (unfiltered by date — loaded once). */
  private allCompleted: WorkOrder[] = [];
  /** Current date-range window in days (0 = all time). */
  private rangeDays: number = DEFAULT_DAYS;
  private isAdmin = false;

  async connectedCallback(): Promise<void> {
    injectStyles();
    this.innerHTML = await loadView(VIEW_URL);
    await this.load();
  }

  private setStatus(message: string): void {
    setText(this, "[data-status]", message);
  }

  private async load(): Promise<void> {
    this.setStatus("Loading completed work orders…");
    let orders: WorkOrder[];
    try {
      const page = await listWorkOrders({ limit: 100, ordering: "-finalized_at" });
      orders = page.results;
    } catch (error) {
      console.error("[mango-completed] failed to load", error);
      this.setStatus("Could not load completed work orders.");
      return;
    }

    const completed = orders.filter((j) => j.provider_status === "complete");
    // Field-presence role gate: derive from any loaded WO (completed first).
    const probe = completed[0] ?? orders[0];
    this.isAdmin = probe ? hasFinancials(probe) : false;
    this.allCompleted = completed;

    // Count pill (total, unfiltered by date).
    const countPill = this.querySelector<HTMLElement>("[data-count]");
    if (countPill) countPill.textContent = `${completed.length} completed`;

    if (this.isAdmin) {
      // Show date-range selector and export button area.
      toggle(this, "[data-daterange-wrap]", true);
      this.wireDateRange();
      this.renderAdmin();
      const exportBtn = this.querySelector<HTMLButtonElement>("[data-export]");
      if (exportBtn && completed.length) {
        exportBtn.hidden = false;
        exportBtn.addEventListener("click", () =>
          this.exportCsv(this.filteredCompleted()),
        );
      }
    } else {
      // Strip every admin-only column (header + cells) for a Staff response.
      this.querySelectorAll(".admin-col").forEach((el) => el.remove());
    }

    this.setStatus("");
  }

  /** Completed jobs within the current date-range window. */
  private filteredCompleted(): WorkOrder[] {
    if (this.rangeDays === 0) return this.allCompleted;
    const cutoff = Date.now() - this.rangeDays * 24 * 60 * 60 * 1000;
    return this.allCompleted.filter((j) => completedAt(j).getTime() >= cutoff);
  }

  /** Wire the date-range dropdown. */
  private wireDateRange(): void {
    const btn = this.querySelector<HTMLButtonElement>("[data-daterange-btn]");
    const menu = this.querySelector<HTMLElement>("[data-daterange-menu]");
    if (!btn || !menu) return;

    // Toggle open/close.
    btn.addEventListener("click", () => {
      const open = menu.hidden;
      menu.hidden = !open;
      btn.setAttribute("aria-expanded", String(open));
    });

    // Close on outside click.
    document.addEventListener(
      "click",
      (e) => {
        if (!this.contains(e.target as Node)) {
          menu.hidden = true;
          btn.setAttribute("aria-expanded", "false");
        }
      },
      { capture: true },
    );

    // Option selection.
    menu.addEventListener("click", (e) => {
      const li = (e.target as Element).closest<HTMLElement>("li[data-days]");
      if (!li) return;
      const days = Number(li.dataset["days"] ?? DEFAULT_DAYS);
      this.rangeDays = days;

      // Update selected state.
      menu.querySelectorAll("li").forEach((el) => el.removeAttribute("aria-selected"));
      li.setAttribute("aria-selected", "true");

      // Update button label.
      const labelEl = btn.querySelector<HTMLElement>("[data-daterange-label]");
      if (labelEl) labelEl.textContent = li.textContent ?? "";

      menu.hidden = true;
      btn.setAttribute("aria-expanded", "false");

      // Re-render with new range.
      this.renderAdmin();
    });
  }

  /** Render the hero, summary cards, and table for the current date range. */
  private renderAdmin(): void {
    const visible = this.filteredCompleted();

    toggle(this, "[data-admin-section]", true);
    toggle(this, "[data-table]", visible.length > 0);

    this.renderHero(visible);
    this.renderCards(visible);
    this.renderRows(visible);
  }

  /** Green hero: sum of per-row total-earnings for the filtered set. */
  private renderHero(visible: WorkOrder[]): void {
    let sumEarnings = 0;
    for (const j of visible) {
      sumEarnings += computeTotalEarnings(j) ?? 0;
    }
    setText(this, "[data-hero-amount]", `$${money(String(sumEarnings))}`);
    setText(this, "[data-hero-range]", rangeLabel(this.rangeDays));
    toggle(this, "[data-hero]", true);
  }

  /** Three summary cards: count · total FMV received · e-waste volume. */
  private renderCards(visible: WorkOrder[]): void {
    let sumFmv = 0;
    let sumEwaste = 0;
    let ewasteKnown = false;
    for (const j of visible) {
      sumFmv += Number(j.fmv_breakdown?.total_assessed_fmv ?? 0);
      if (j.weight_ewaste != null) {
        sumEwaste += Number(j.weight_ewaste);
        ewasteKnown = true;
      }
    }
    setText(this, "[data-sum-count]", String(visible.length));
    setText(this, "[data-sum-fmv]", `$${money(String(sumFmv))}`);
    setText(
      this,
      "[data-sum-ewaste]",
      ewasteKnown
        ? `${sumEwaste.toLocaleString("en-US", { maximumFractionDigits: 0 })} lbs`
        : "— lbs",
    );
  }

  /** Populate the table rows for the filtered set. */
  private renderRows(visible: WorkOrder[]): void {
    const list = this.querySelector<HTMLElement>("[data-list]");
    const tpl = this.querySelector<HTMLTemplateElement>("template[data-row]");
    if (!list || !tpl) return;

    list.replaceChildren();
    for (const job of visible) {
      const frag = tpl.content.cloneNode(true) as DocumentFragment;
      applyBindings(frag, { job });
      setText(frag, "[data-site]", job.pickup_region ?? "—");
      setText(frag, "[data-completed]", completedOn(job));

      // Status badge — settled once Mango approved, pending until then.
      const settled = !!job.mango_approved_at;
      const statusCell = frag.querySelector<HTMLElement>("[data-status-cell]");
      if (statusCell) {
        const badge = document.createElement("span");
        badge.className = settled ? "settled-badge" : "pending-badge";
        badge.textContent = settled ? "Settled" : "Pending ACH";
        statusCell.appendChild(badge);
      }

      // Financial columns (Admin-only fields).
      const totalEarnings = computeTotalEarnings(job);
      setText(
        frag,
        "[data-total-earnings]",
        totalEarnings != null ? `$${money(String(totalEarnings))}` : "—",
      );

      // Overage earnings: provider_overage_comp — shown POSITIVE (it's vendor comp).
      const overageComp = Number(job.net_settlement?.provider_overage_comp ?? 0);
      const overageEl = frag.querySelector<HTMLElement>("[data-overage-earnings]");
      if (overageEl) {
        overageEl.textContent = overageComp > 0 ? `$${money(String(overageComp))}` : "—";
        // Green when positive, muted when zero/absent.
        overageEl.style.color = overageComp > 0 ? "var(--green)" : "var(--text-3)";
      }

      // Total FMV value (neutral) — the full assessed FMV (FMV1 total).
      const fmv = job.fmv_breakdown?.total_assessed_fmv;
      setText(frag, "[data-fmv]", fmv != null ? `$${money(fmv)}` : "—");

      // Equipment purchase price (neutral) — what the provider pays Mango.
      const equipPrice = job.net_settlement?.equipment_purchase_price;
      setText(frag, "[data-equip-price]", equipPrice != null ? `$${money(equipPrice)}` : "—");

      // Reopen button.
      const reopen = frag.querySelector<HTMLElement>("[data-reopen]");
      if (reopen) {
        const fire = (): void => this.requestReopen(job.short_name);
        reopen.addEventListener("click", fire);
        reopen.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fire();
          }
        });
      }

      list.appendChild(frag);
    }

    if (visible.length === 0) {
      this.setStatus(`No completed work orders in the ${rangeLabel(this.rangeDays)}.`);
    } else {
      this.setStatus("");
    }
  }

  /** MOCK: there is no reopen/settlement-status backend yet (billing phase). */
  private requestReopen(shortName: string): void {
    this.setStatus(`Reopen requested for ${shortName}. Mango ops will review and follow up.`);
  }

  /** Build the settlement-history CSV client-side and trigger a download. */
  private exportCsv(completed: WorkOrder[]): void {
    const header = [
      "Work order",
      "Site",
      "Completed",
      "Total earnings",
      "Overage earnings",
      "Total FMV value",
      "Equipment purchase price",
      "Status",
    ];
    const rows = completed.map((j) => {
      const totalEarnings = computeTotalEarnings(j);
      return [
        j.short_name,
        j.pickup_region ?? "",
        completedOn(j),
        totalEarnings != null ? String(totalEarnings.toFixed(2)) : "",
        j.net_settlement?.provider_overage_comp ?? "",
        j.fmv_breakdown?.total_assessed_fmv ?? "",
        j.net_settlement?.equipment_purchase_price ?? "",
        j.mango_approved_at ? "Settled" : "Pending ACH",
      ];
    });
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\r\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "completed-work-orders.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export function registerMangoCompleted(): void {
  if (!customElements.get("mango-completed")) {
    customElements.define("mango-completed", MangoCompleted);
  }
}
